import type { ReactNode } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import Animated from 'react-native-reanimated';

import { assertHitOnLayout } from '../a11y';
import { usePressMotion } from '../theme/motion';
import { colors, fontScaleCap, hit, radii, shadows, strokes } from '../tokens';
import { reportBlankButton, resolveButtonLabel } from './buttonLabel';
import { Text, type TextTone } from './Text';

export type WasabiVariant = 'primary' | 'outline' | 'text';
export type WasabiSize = 'md' | 'sm';

export interface WasabiButtonProps {
  /**
   * What the face draws. A label that is empty (or whitespace) is a BUG, not a state: the control does not
   * draw at all, and says so in dev. See `buttonLabel.ts` for the three screens that shipped a blank wasabi
   * plate and why a fallback string is not the fix.
   */
  label: string;
  onPress?: () => void;
  variant?: WasabiVariant;
  /** md = the sticky CTA (56 tall, 4px plate, 17/700); sm = inline ("Approve all": 40 tall, 3px plate, 14/700, radius 12). */
  size?: WasabiSize;
  disabled?: boolean;
  loading?: boolean;
  /** Leading glyph slot (Apple / Google marks on P02). */
  leading?: ReactNode;
  /** Text-variant colour (link by default; white on the gradient). */
  tone?: TextTone;
  /**
   * Label size. text: sm 13/700 (P06 links) · md 14/700 (default) · lg 15/600 (under the P01 CTA).
   * outline: sm 14/700 (the half-width P06 pair) · md 15/700. Ignored by primary and by size="sm".
   */
  textSize?: 'sm' | 'md' | 'lg';
  /** Stretch to the container width (default) or hug the label. */
  fullWidth?: boolean;
  /** Face height override — an outline paired with a primary (P08) matches its 56. */
  faceHeight?: number;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
}

/**
 * The primary CTA: wasabi fill, 2px #170909 border, hard shadow 0 4px 0 0 #170909; press = face down 2px while the
 * plate collapses 4 → 2 (120 ms on the one curve). Outline = white face, 2px slate border, no plate (48 tall).
 * Text = link label only, 44 tall hit box. Radius 16 (board) for md faces; 12 for the inline sm button.
 */
export function WasabiButton({
  label,
  onPress,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  leading,
  tone,
  textSize = 'md',
  fullWidth = true,
  faceHeight,
  style,
  accessibilityLabel,
  testID,
}: WasabiButtonProps) {
  const primary = variant === 'primary';
  const plate = primary ? (size === 'md' ? shadows.md : shadows.sm) : 0;
  const { faceStyle, onPressIn, onPressOut } = usePressMotion(plate ? shadows.pressDrop : 0);
  const height = faceHeight ?? (size === 'md' ? (primary ? 56 : 48) : 40);
  const radius = size === 'md' ? radii.button : radii.input;
  const inactive = disabled || loading;
  /**
   * The face's content, before anything is drawn. `leading` and `loading` both put something in the face that
   * is not the label, so either one makes an empty label legitimate — provided the control is still NAMED.
   * Nothing to show or nothing to say and this returns `null`: see `buttonLabel.ts`.
   */
  const resolved = resolveButtonLabel(`WasabiButton "${label}"`, {
    label,
    accessibilityLabel,
    faceContent: Boolean(leading) || loading,
  });
  if (!resolved.draws) {
    reportBlankButton(resolved);
    return null;
  }
  const a11y = resolved.accessibilityLabel ?? label;

  if (variant === 'text') {
    return (
      <Pressable
        onPress={onPress}
        disabled={inactive}
        accessibilityRole="button"
        accessibilityLabel={a11y}
        accessibilityState={{ disabled: inactive }}
        onLayout={assertHitOnLayout(`WasabiButton.text "${label}"`, hit.parent)}
        testID={testID}
        style={[
          styles.textButton,
          { alignSelf: fullWidth ? 'stretch' : 'center' },
          inactive && styles.dim,
          style,
        ]}
      >
        <Text
          variant={textSize === 'lg' ? 'textLinkLg' : textSize === 'sm' ? 'textLinkSm' : 'textLink'}
          tone={tone ?? 'link'}
        >
          {resolved.text}
        </Text>
      </Pressable>
    );
  }

  // The sm face (40 + 3 plate) is padded at the top to a real 44 box — no hitSlop, so parents never clip it.
  const padTop = Math.max(0, hit.parent - (height + plate));
  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={inactive}
      accessibilityRole="button"
      accessibilityLabel={a11y}
      accessibilityState={{ disabled: inactive, busy: loading }}
      onLayout={assertHitOnLayout(`WasabiButton "${label}"`, hit.parent)}
      testID={testID}
      style={[
        {
          paddingTop: padTop,
          paddingBottom: plate,
          // 'center', not 'flex-start': alignSelf is the CROSS axis, so inside a row this is vertical
          // placement and flex-start pins the button to the top. Matches the text path below and ChildButton.
          alignSelf: fullWidth ? 'stretch' : 'center',
        },
        inactive && styles.dim,
        style,
      ]}
    >
      {plate > 0 ? (
        <View style={[styles.plate, { top: plate + padTop, borderRadius: radius }]} />
      ) : null}
      <Animated.View
        style={[
          styles.face,
          {
            /*
             * A FLOOR, NOT A HEIGHT (Evan, 28 Sep: "containers grow — minHeight, not height"; 14 Oct, large text).
             * At the phone's larger text sizes on a small phone a label like "Start Money Meeting" takes two
             * lines; a fixed face clipped the second. The drawn height is unchanged at standard text.
             */
            minHeight: height,
            paddingVertical: 6,
            borderRadius: radius,
            paddingHorizontal: size === 'md' ? 20 : 14,
            backgroundColor: primary ? colors.wasabi : colors.white,
            borderColor: primary ? colors.ctaBorder : colors.slate,
          },
          faceStyle,
        ]}
      >
        {loading ? (
          <ActivityIndicator color={colors.ink} />
        ) : (
          <View style={styles.content}>
            {leading ? <View style={styles.leading}>{leading}</View> : null}
            {/* null only for a glyph-only face, which is named by its accessibilityLabel above. */}
            {resolved.text !== null ? (
              <Text
                variant={
                  size === 'sm'
                    ? 'buttonSm'
                    : primary
                      ? 'button'
                      : textSize === 'sm'
                        ? 'buttonOutlineSm'
                        : 'buttonOutline'
                }
                tone="ink"
                style={styles.label}
                maxFontSizeMultiplier={fontScaleCap.control}
              >
                {resolved.text}
              </Text>
            ) : null}
          </View>
        )}
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  plate: { position: 'absolute', left: 0, right: 0, bottom: 0, backgroundColor: colors.ctaBorder },
  face: { borderWidth: strokes.cta, alignItems: 'center', justifyContent: 'center' },
  content: { flexDirection: 'row', alignItems: 'center', gap: 10, maxWidth: '100%' },
  label: { textAlign: 'center', flexShrink: 1 },
  leading: { width: 18, height: 18, alignItems: 'center', justifyContent: 'center' },
  textButton: {
    minHeight: hit.parent,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  dim: { opacity: 0.5 },
});
